/**
 * Paper and grid backgrounds (SRS C7, docs/06-UI-SPEC.md §6.5).
 *
 * The background lives on its own canvas layer beneath everything else and is never
 * selectable — it is paper, not content. It is drawn in canvas space so the grid pans
 * and zooms with the work rather than sliding underneath it.
 */

import type { PageBackground } from '@opal/board-doc';
import { canvasToScreen, visibleCanvasRect, type Viewport } from '@opal/shared';

/** Lines closer together than this on screen read as a grey wash, so they are dropped. */
const MIN_SCREEN_SPACING_PX = 6;

/** A hard stop on how much grid one frame may draw, for the Chromebook's sake. */
const MAX_LINES_PER_AXIS = 400;

/** Every fifth line of graph paper is the heavier one. */
const GRAPH_MAJOR_EVERY = 5;

/**
 * Spacing to actually draw at.
 *
 * Zoomed out far enough, every grid becomes a solid block of ink. Doubling the
 * spacing until the lines are far enough apart keeps the paper readable at 10% zoom
 * without changing what the page is set to.
 */
export function effectiveSpacing(gridSize: number, zoom: number): number {
  let spacing = Math.max(gridSize, 1);
  while (spacing * zoom < MIN_SCREEN_SPACING_PX) spacing *= 2;
  return spacing;
}

/** Grid positions covering `from` to `to`, in canvas units. */
export function gridPositions(from: number, to: number, spacing: number): number[] {
  if (spacing <= 0 || to < from) return [];
  const first = Math.floor(from / spacing) * spacing;
  const count = Math.min(Math.ceil((to - first) / spacing) + 1, MAX_LINES_PER_AXIS);
  return Array.from({ length: Math.max(count, 0) }, (_, index) => first + index * spacing);
}

interface Paper {
  readonly ctx: CanvasRenderingContext2D;
  readonly viewport: Viewport;
  readonly spacing: number;
  readonly color: string;
}

function line(paper: Paper, from: { x: number; y: number }, to: { x: number; y: number }): void {
  const a = canvasToScreen(paper.viewport, from);
  const b = canvasToScreen(paper.viewport, to);
  paper.ctx.beginPath();
  paper.ctx.moveTo(a.x, a.y);
  paper.ctx.lineTo(b.x, b.y);
  paper.ctx.stroke();
}

function drawSquare(paper: Paper, vertical: boolean, horizontal: boolean): void {
  const area = visibleCanvasRect(paper.viewport);
  const right = area.x + area.width;
  const bottom = area.y + area.height;
  if (vertical) {
    for (const x of gridPositions(area.x, right, paper.spacing)) {
      line(paper, { x, y: area.y }, { x, y: bottom });
    }
  }
  if (horizontal) {
    for (const y of gridPositions(area.y, bottom, paper.spacing)) {
      line(paper, { x: area.x, y }, { x: right, y });
    }
  }
}

function drawDots(paper: Paper): void {
  const area = visibleCanvasRect(paper.viewport);
  const radius = Math.min(Math.max(paper.viewport.zoom, 0.5), 2);
  paper.ctx.fillStyle = paper.color;
  for (const x of gridPositions(area.x, area.x + area.width, paper.spacing)) {
    for (const y of gridPositions(area.y, area.y + area.height, paper.spacing)) {
      const at = canvasToScreen(paper.viewport, { x, y });
      paper.ctx.beginPath();
      paper.ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
      paper.ctx.fill();
    }
  }
}

function drawGraph(paper: Paper): void {
  drawSquare(paper, true, true);
  paper.ctx.lineWidth = 1.5;
  drawSquare({ ...paper, spacing: paper.spacing * GRAPH_MAJOR_EVERY }, true, true);
}

function drawCoordinate(paper: Paper): void {
  drawSquare(paper, true, true);
  const area = visibleCanvasRect(paper.viewport);
  paper.ctx.lineWidth = 2;
  // The axes of the page, through the canvas origin.
  line(paper, { x: area.x, y: 0 }, { x: area.x + area.width, y: 0 });
  line(paper, { x: 0, y: area.y }, { x: 0, y: area.y + area.height });
}

function drawIsometric(paper: Paper): void {
  const area = visibleCanvasRect(paper.viewport);
  const right = area.x + area.width;
  const bottom = area.y + area.height;
  // Vertical lines, plus the two 30° families that make an isometric sheet.
  drawSquare(paper, true, false);
  const rise = Math.tan(Math.PI / 6);
  const reach = area.width * rise;
  for (const y of gridPositions(area.y - reach, bottom + reach, paper.spacing)) {
    line(paper, { x: area.x, y }, { x: right, y: y + area.width * rise });
    line(paper, { x: area.x, y }, { x: right, y: y - area.width * rise });
  }
}

function drawPolar(paper: Paper): void {
  const area = visibleCanvasRect(paper.viewport);
  const centre = canvasToScreen(paper.viewport, { x: 0, y: 0 });
  const reach = Math.hypot(area.width, area.height);
  const ring = paper.spacing * paper.viewport.zoom;

  for (let radius = ring; radius <= reach * paper.viewport.zoom; radius += ring) {
    paper.ctx.beginPath();
    paper.ctx.arc(centre.x, centre.y, radius, 0, Math.PI * 2);
    paper.ctx.stroke();
  }
  for (let degrees = 0; degrees < 180; degrees += 30) {
    const angle = (degrees * Math.PI) / 180;
    line(
      paper,
      { x: -reach * Math.cos(angle), y: -reach * Math.sin(angle) },
      { x: reach * Math.cos(angle), y: reach * Math.sin(angle) },
    );
  }
}

/** Draws the page's paper. `blank` draws nothing at all. */
export function drawBackground(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  background: PageBackground,
): void {
  if (background.type === 'blank' || viewport.widthPx === 0) return;

  const paper: Paper = {
    ctx,
    viewport,
    spacing: effectiveSpacing(background.gridSize, viewport.zoom),
    color: background.color,
  };

  ctx.save();
  ctx.strokeStyle = background.color;
  ctx.fillStyle = background.color;
  ctx.lineWidth = 1;

  if (background.type === 'square') drawSquare(paper, true, true);
  if (background.type === 'lined') drawSquare(paper, false, true);
  if (background.type === 'dot') drawDots(paper);
  if (background.type === 'graph') drawGraph(paper);
  if (background.type === 'coordinate') drawCoordinate(paper);
  if (background.type === 'isometric') drawIsometric(paper);
  if (background.type === 'polar') drawPolar(paper);

  ctx.restore();
}
